import { GetSessionsType } from "../profile/Profile.gql";
import { AiExercise, AiExerciseId, Category, DbSession, Exercise, ExerciseId, ExerciseIdWithName, ExerciseInSession } from "../types";

export type SessionSummaryCategoryRecord = {
    [K in Category]?: { exercises: ExerciseIdWithName[]; duration: number };
};

export const getSummaryFromSession = (session: DbSession): SessionSummaryCategoryRecord => {
    const exercisesByCategory = session.exercises.reduce<SessionSummaryCategoryRecord>(
        (acc, exercise) => ({
            ...acc,
            [exercise.category]: {
                exercises: (acc[exercise.category]?.exercises ?? []).concat(exercise),
                duration: getDurationForCategory(session, exercise.category),
            },
        }),
        {},
    );

    const aiExercises = session.aiExercises.map((id) => ({ id, nameEn: getAiExerciseNameFromId(id), nameDe: getAiExerciseNameFromId(id) }));

    return { ...exercisesByCategory, [Category.Ai]: { exercises: aiExercises, duration: session.durationAi } };
};

type GetSessionsTypeExercise = GetSessionsType["sessions"]["nodes"][number]["exercises"][number];

export const getExercisesByCategory = <T extends GetSessionsTypeExercise>(exercises: ReadonlyArray<T>) =>
    exercises.reduce(
        (exercisesByCategory: { [K in Category]?: T[] }, exercise) => ({
            ...exercisesByCategory,
            [exercise.category]: (exercisesByCategory[exercise.category] || []).concat(exercise),
        }),
        {},
    );

export const getAllExercisesDuration = (exercises: ReadonlyArray<ExerciseInSession>) => {
    return exercises.reduce((acc, exercise) => acc + exercise.duration, 0);
};

export const getDurationForCategory = (session: DbSession, category: Category) => {
    switch (category) {
        case Category.Endurance:
            return session.durationEndurance;
        case Category.Strength:
            return session.durationStrength;
        case Category.Coordination:
            return session.durationCoordination;
        case Category.Relaxation:
            return session.durationRelaxation;
        case Category.Mobility:
            return session.durationMobility;
        case Category.Ai:
            return session.durationAi;
        default:
            return 0;
    }
};

export function isAiExerciseId(id: ExerciseId): id is AiExerciseId {
    return typeof id === "string" && id.startsWith("ai-");
}

export function isAiExercise(exercise: ExerciseInSession): exercise is AiExercise & { duration: number } {
    return isAiExerciseId(exercise.id);
}

export function isTraditionalExerciseId(id: ExerciseId): id is number {
    return typeof id === "number";
}
export function isTraditionalExercise(exercise: ExerciseInSession): exercise is Exercise & { duration: number } {
    return isTraditionalExerciseId(exercise.id);
}

export function getAiExerciseNameFromId(id: AiExerciseId): string {
    return id.slice(3);
}

export function getAiExerciseIdFromName(name: string): AiExerciseId {
    return `ai-${name}` as AiExerciseId;
}
